import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ArrowLeft, ArrowRight, CheckCircle2, HelpCircle, Mail, ShieldCheck } from 'lucide-react';
import { BrandLogo } from './BrandLogo';
import { LegalFooter } from './LegalFooter';
import { categoryLabel, commercialModeLabel, formatEuro, recommendProductsFromQuiz } from '../data/liveDrop';
import { trackEvent } from '../lib/analytics';

const playContextOptions = [
  { key: 'any', label: 'Nog niet zeker', help: 'Laat keuzehulp breed zoeken.' },
  { key: 'indoor', label: 'Indoor / sporthal', help: 'Ballen en sets die foutaankoop voorkomen.' },
  { key: 'outdoor', label: 'Outdoor / park', help: 'Focus op outdoorballen, netten en recreatiesets.' }
];

const buyerTypeOptions = [
  { key: 'consumer', label: 'Voor mij/gezin', help: 'Startersets, paddles en verbruiksartikelen.' },
  { key: 'club', label: 'Club/school/bedrijf', help: 'Offerte en bundelroute eerst.' }
];

const experienceOptions = [
  { key: 'starter', label: 'Beginner', help: 'Laagdrempelig starten.' },
  { key: 'recreatief', label: 'Recreatief', help: 'Betere keuze zonder premium risico.' },
  { key: 'intermediate', label: 'Groeiende speler', help: 'Meer controle, gevoel en gerichter kiezen.' },
  { key: 'any', label: 'Nog onbekend', help: 'Brede aanbeveling.' }
];

export function ChoiceQuizPage() {
  const [playContext, setPlayContext] = useState('any');
  const [buyerType, setBuyerType] = useState('consumer');
  const [experience, setExperience] = useState('starter');
  const [started, setStarted] = useState(false);

  useEffect(() => {
    document.title = 'Pickleball keuzehulp | ProPickleball.nl';
    const description = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (description) {
      description.content = 'Kies sneller tussen pickleball starterset, paddle, ballen, net of clubpakket met de ProPickleball.nl keuzehulp.';
    }
  }, []);

  const recommendations = useMemo(() => recommendProductsFromQuiz(playContext, buyerType, experience), [buyerType, experience, playContext]);

  function markStarted(label: string) {
    if (!started) {
      trackEvent({ event: 'quiz_started', location: 'choice_quiz', label, routeFamily: 'quiz' });
      setStarted(true);
    }
  }

  function selectOption(kind: string, value: string, setter: (value: string) => void) {
    markStarted(kind);
    setter(value);
  }

  function trackResultClick(sku: string, name: string, category: string, brand: string) {
    trackEvent({ event: 'quiz_completed', location: 'choice_quiz', label: name, sku, category, brand, routeFamily: 'quiz' });
    trackEvent({ event: 'select_item', location: 'choice_quiz_result', label: name, sku, category, brand, routeFamily: 'quiz' });
  }

  return (
    <>
      <main className="choiceQuizPage">
        <nav className="productTopbar" aria-label="Keuzehulp navigatie">
          <BrandLogo compact />
          <a className="backHome" href="/"><ArrowLeft size={18} /> Terug naar home</a>
        </nav>

        <section className="quizHero">
          <p className="eyebrow">Keuzehulp</p>
          <h1>Vind sneller je eerste pickleball product.</h1>
          <p>Beantwoord drie vragen en krijg een passend startpunt. Producten zijn nog niet direct te bestellen, maar je kunt wel alvast een update aanvragen.</p>
        </section>

        <section className="quizShell">
          <div className="quizQuestions">
            <QuizGroup title="Waar speel je vooral?" icon={<HelpCircle />} options={playContextOptions} value={playContext} onChange={(value) => selectOption('play_context', value, setPlayContext)} />
            <QuizGroup title="Voor wie koop je?" icon={<ShieldCheck />} options={buyerTypeOptions} value={buyerType} onChange={(value) => selectOption('buyer_type', value, setBuyerType)} />
            <QuizGroup title="Wat is je niveau?" icon={<CheckCircle2 />} options={experienceOptions} value={experience} onChange={(value) => selectOption('experience', value, setExperience)} />
          </div>

          <aside className="quizResults" aria-label="Aanbevolen producten">
            <div className="panelHeading">
              <CheckCircle2 />
              <div>
                <p className="eyebrow">Advies</p>
                <h2>Beste startpunt voor deze keuze.</h2>
              </div>
            </div>
            <div className="quizResultList">
              {recommendations.map((product) => (
                <a href={product.pdpPath} key={product.sku} onClick={() => trackResultClick(product.sku, product.name, product.category, product.brand)}>
                  <span>{categoryLabel(product.category)} / {commercialModeLabel(product.commercialMode)}</span>
                  <strong>{product.name}</strong>
                  <small>{formatEuro(product.marketResearch.targetPriceInclVat)} verwachte prijs</small>
                  <ArrowRight size={16} />
                </a>
              ))}
            </div>
            <p className="formNote" role="status">
              Bestellen en voorraad tonen staat nog uit. Dit advies is een startpunt; de eerste verkoopbatch volgt pas na gecontroleerde product- en leveringsinformatie.
            </p>
            {buyerType === 'club' ? (
              <a className="primaryCta quizMailCta" href="/pickleball-clubpakket/" onClick={() => trackEvent({ event: 'cta_click', location: 'choice_quiz', label: 'club_package_advice', placement: 'quiz_result', routeFamily: 'quiz' })}>
                <ArrowRight size={18} /> Naar club- en schoolpakketadvies
              </a>
            ) : (
              <a className="primaryCta quizMailCta" href="/#nieuwsbrief" aria-label="Beschikbaarheidsupdate aanvragen" onClick={() => trackEvent({ event: 'cta_click', location: 'choice_quiz', label: 'launch_list_anchor', placement: 'quiz_result', routeFamily: 'quiz' })}>
                <Mail size={18} /> Op de launchlijst voor updates
              </a>
            )}
          </aside>
        </section>
      </main>
      <LegalFooter />
    </>
  );
}

function QuizGroup({
  icon,
  onChange,
  options,
  title,
  value
}: {
  icon: ReactNode;
  onChange: (value: string) => void;
  options: Array<{ key: string; label: string; help: string }>;
  title: string;
  value: string;
}) {
  return (
    <fieldset className="quizGroup">
      <legend>{icon}{title}</legend>
      <div>
        {options.map((option) => (
          <button
            aria-pressed={value === option.key}
            className={value === option.key ? 'active' : ''}
            key={option.key}
            onClick={() => onChange(option.key)}
            type="button"
          >
            <strong>{option.label}</strong>
            <span>{option.help}</span>
          </button>
        ))}
      </div>
    </fieldset>
  );
}
